import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  ActivatedRouterStateSnapshot,
  Breadcrumb,
  ProductSearchPage,
  RoutingService
} from '@spartacus/core';
import { FacetList, ProductListComponentService } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { filter, map, pluck, switchMap } from 'rxjs/operators';

/**
 * Provides access to all the facets and active facets for the Product Listing Page.
 */
@Injectable({
  providedIn: 'root',
})
export class UigProductFacetService {
  protected readonly routeState$ = this.routing
    .getRouterState()
    .pipe(pluck('state'));

  protected readonly searchResult$: Observable<ProductSearchPage> = this.routeState$.pipe(
    switchMap((state) =>
      this.productListComponentService.model$.pipe(
        filter((page) => this.filterForPage(state, page)),
        map((page) => this.mapResults(state, page))
      )
    )
  );

  constructor(
    protected routing: RoutingService,
    protected productListComponentService: ProductListComponentService
  ) {
  }

  /**
   * Observes the facets and active facets for the given page. The facet data
   * is provided in a `FacetList`.
   */
  readonly facetList$: Observable<FacetList> = this.searchResult$.pipe(
    map(
      (result: ProductSearchPage) =>
        ({
          facets: result.facets,
          activeFacets: result.breadcrumbs,
        } as FacetList)
    )
  );

  /**
   * Filters the current result by verifying if the result is related to the page.
   * This is done to avoid a combination of the next page and the current search results.
   */
  protected filterForPage(
    state: ActivatedRouterStateSnapshot,
    page: ProductSearchPage
  ): boolean {

    if (!page.currentQuery?.query?.value) {
      return false;
    }
    return true;
  }

  private mapResults(
    state: ActivatedRouterStateSnapshot,
    page: ProductSearchPage
  ): ProductSearchPage {
    return {
      ...page,
      breadcrumbs: this.filterBreadcrumbs(page.breadcrumbs, state.params),
    };
  }

  /**
   * filter breadcrumbs which are not actively selected
   * but coming from the route navigation
   */
  private filterBreadcrumbs(
    breadcrumbs: Breadcrumb[],
    params: Params
  ): Breadcrumb[] {
    return breadcrumbs
      ? breadcrumbs.filter(
        (breadcrumb) => !this.hasBreadcrumb(breadcrumb, params)
      )
      : [];
  }

  /**
   * Indicates whether the breadcrumb is related to navigation parameters,
   * since either the category or brand code should match those codes.
   */
  private hasBreadcrumb(breadcrumb: Breadcrumb, params: Params): boolean {
    return (
      breadcrumb.facetCode === 'allCategories' &&
      (breadcrumb.facetValueCode === params.categoryCode ||
        breadcrumb.facetValueCode === params.brandCode)
    );
  }
}
